import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import { showNotification } from "aldoo-ra/Notification"
import { CheckIcon, EnvelopeIcon } from "@heroicons/react/24/solid"
import NewsletterAPI from "../../api"

const StepHeader = ({ steps, currentStep, completedSteps, onStepClick, formData, audiencePreview }) => {
  const [testEmails, setTestEmails] = useState("")
  const [sendingTest, setSendingTest] = useState(false)

  const sendTestEmail = async () => {
    if (!testEmails.trim() || formData.templates.length === 0) return
    
    try {
      setSendingTest(true)
      const emails = testEmails.split(',').map(email => email.trim()).filter(email => email)
      const firstTemplate = formData.templates[0]
      
      if (!firstTemplate?.templateId) {
        showNotification({
          message: 'Please select at least one template first',
          className: "bg-admin_error text-white"
        })
        return
      }
      
      const response = await NewsletterAPI.sendTestEmail(
        firstTemplate.templateId,
        emails,
        {
          testMode: true,
          previewData: {
            segments: formData.segments,
            audienceSize: audiencePreview?.audienceSize || 0
          }
        }
      )
      
      if (response.success) {
        showNotification({
          message: `Test email sent to ${emails.length} address${emails.length > 1 ? 'es' : ''}`,
          className: "bg-admin_success text-white"
        })
        setTestEmails("")
      } else {
        showNotification({
          message: 'Failed to send test email: ' + (response.message || 'Unknown error'),
          className: "bg-admin_error text-white"
        })
      }
    } catch (error) {
      console.error('Failed to send test email:', error)
      showNotification({
        message: 'Failed to send test email: ' + error.message,
        className: "bg-admin_error text-white"
      })
    } finally {
      setSendingTest(false)
    }
  }
  return (
    <div className="px-6 py-4 bg-admin_paper">
      {/* Step Navigation */}
      <div className="flex items-center justify-between max-w-4xl mx-auto mb-4">
        {steps.map((step, index) => {
          const isActive = step.id === currentStep
          const isCompleted = completedSteps && completedSteps.has(step.id)

          return (
            <div key={step.id} className="flex items-center">
              {/* Step Circle */}
              <div
                className="flex items-center cursor-pointer transition-colors"
                onClick={() => onStepClick(step.id)}
              >
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                    ${isCompleted 
                      ? "bg-admin_success text-white" 
                      : isActive 
                        ? "bg-admin_primary text-white" 
                        : "bg-admin_neutral_3 text-white"
                    }
                  `}
                >
                  {isCompleted ? (
                    <CheckIcon className="w-5 h-5" />
                  ) : (
                    step.id
                  )}
                </div>

                <div className="ml-3">
                  <Typography 
                    variant="admin_label" 
                    className={`font-medium my-0 ${
                      isActive ? "text-admin_primary" : "text-admin_text"
                    }`}
                  >
                    {step.title}
                  </Typography>

                  <Typography variant="admin_txt2_dim">
                    {step.description}
                  </Typography>
                </div>
              </div>
              
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div 
                  className={`
                    flex-1 h-0.5 mx-4 transition-colors
                    ${isCompleted ? "bg-admin_success" : "bg-admin_neutral_3"}
                  `}
                />
              )}
            </div>
          )
        })}
      </div>

      {/* Test Email Section - Only show on step 4 */}
      {currentStep === 4 && (
        <div className="max-w-4xl mx-auto border-t border-admin_bordered pt-4">
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <EnvelopeIcon className="w-5 h-5 text-admin_primary mr-2" />

              <Typography 
                variant="admin_label" 
                className="my-0"
              >
                Send Test Email:
              </Typography>
            </div>

            <div className="flex-1 max-w-md">
              <Input
                placeholder="email1@example.com, email2@example.com"
                value={testEmails}
                onChange={(e) => setTestEmails(e.target.value)}
                disabled={formData?.templates?.length === 0 || sendingTest}
                className="text-sm"
              />
            </div>

            <Button
              variant="admin_secondary"
              onClick={sendTestEmail}
              disabled={!testEmails.trim() || formData?.templates?.length === 0 || sendingTest}
              className="ml-auto"
            >
              <EnvelopeIcon className="w-4 h-4" />
              {!sendingTest && <span className="ml-1">Send</span>}
              {sendingTest && <span className="ml-1">Sending...</span>}
            </Button>
          </div>
          
          {formData?.templates?.length === 0 && (
            <Typography 
              variant="admin_txt2_dim" 
              className="block mt-2 text-center"
            >
              Please select at least one template to send test emails
            </Typography>
          )}
        </div>
      )}
    </div>
  )
}

export default StepHeader